/**
 * Admin Clips - View and manage all video clips
 * Shows latest 100 clips with filters by club, user, status, date
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, ArrowLeft, Filter, Search, RefreshCw, 
  CheckCircle, Clock, XCircle, AlertTriangle,
  Building2, User, Calendar, ChevronDown, ChevronUp,
  ExternalLink, Copy, Check, Video, Trash2, Download,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';

const STATUS_CONFIG = {
  ready: { label: 'Listo', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  processing: { label: 'Procesando', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  pending: { label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  retrying: { label: 'Reintentando', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: RefreshCw },
  failed: { label: 'Fallido', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  error: { label: 'Error', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle }
};

export default function AdminClips() {
  const navigate = useNavigate();
  
  const [clips, setClips] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filterClub, setFilterClub] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail modal
  const [selectedClip, setSelectedClip] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Expanded rows
  const [expandedClip, setExpandedClip] = useState(null);
  
  // Cleanup states
  const [cleaningErrors, setCleaningErrors] = useState(false);
  const [cleaningOld, setCleaningOld] = useState(false);

  const loadClips = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (filterClub !== 'all') params.append('club_id', filterClub);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const res = await api.get(`/admin/clips?${params.toString()}`);
      
      setClips(res.data.clips || []);
      setClubs(res.data.clubs || []);
      setStats(res.data.stats || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error loading clips:', err);
      toast.error('Error al cargar clips');
    } finally {
      setLoading(false);
    }
  }, [filterClub, filterStatus]);

  useEffect(() => {
    loadClips();
  }, [loadClips]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    return `${seconds}s`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Error al copiar');
    }
  };

  // Cleanup functions
  const handleDeleteErrorClips = async () => {
    if (!confirm('¿Eliminar todos los clips con ERROR? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      setCleaningErrors(true);
      const res = await api.post('/admin/clips/delete-errors', { max_days: 0 });
      toast.success(`${res.data.deleted_count} clips con error eliminados`);
      loadClips(); // Refresh list
    } catch (err) {
      console.error('Error deleting error clips:', err);
      toast.error('Error al eliminar clips');
    } finally {
      setCleaningErrors(false);
    }
  };

  const handleCleanupOldClips = async () => {
    if (!confirm('¿Eliminar TODOS los clips mayores a 72 horas? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      setCleaningOld(true);
      const res = await api.post('/admin/clips/cleanup-all-old', { max_days: 3 });
      toast.success(`${res.data.deleted_count} clips antiguos eliminados`);
      loadClips(); // Refresh list
    } catch (err) {
      console.error('Error cleaning up old clips:', err);
      toast.error('Error al limpiar clips');
    } finally {
      setCleaningOld(false);
    }
  };

  const openDetail = async (clip) => {
    setSelectedClip(clip);
    setShowDetailModal(true);
  };

  // Filter clips by search term
  const filteredClips = clips.filter(clip => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      clip.club_name?.toLowerCase().includes(search) ||
      clip.id?.toLowerCase().includes(search) ||
      clip.user_id?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                Clips Generados
              </h1>
              <p className="text-sm text-muted-foreground">
                {total} clips en total
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadClips}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <Video className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.ready || 0}</p>
                <p className="text-xs text-muted-foreground">Listos</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.processing || 0}</p>
                <p className="text-xs text-muted-foreground">Procesando</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.failed || 0}</p>
                <p className="text-xs text-muted-foreground">Fallidos</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filtros:</span>
            </div>
            
            <div className="flex-1 min-w-[200px] max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por club, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            
            <Select value={filterClub} onValueChange={setFilterClub}>
              <SelectTrigger className="w-[180px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Club" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clubs</SelectItem>
                {clubs.map(club => (
                  <SelectItem key={club.id} value={club.id}>
                    {club.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ready">Listo</SelectItem>
                <SelectItem value="processing">Procesando</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="retrying">Reintentando</SelectItem>
                <SelectItem value="failed">Fallido</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Cleanup Actions */}
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
            <span className="text-sm text-muted-foreground mr-2">Limpieza:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeleteErrorClips}
              disabled={cleaningErrors}
              className="text-red-400 border-red-500/30 hover:bg-red-500/10"
              data-testid="delete-error-clips-btn"
            >
              {cleaningErrors ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Eliminar con Error
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCleanupOldClips}
              disabled={cleaningOld}
              className="text-orange-400 border-orange-500/30 hover:bg-orange-500/10"
              data-testid="cleanup-old-clips-btn"
            >
              {cleaningOld ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Limpiar Antiguos (&gt;72h)
            </Button>
            <span className="text-xs text-muted-foreground ml-2">
              (Automático cada 12h)
            </span>
          </div>
        </div>

        {/* Clips List */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Cargando clips...</p>
            </div>
          ) : filteredClips.length === 0 ? (
            <div className="p-12 text-center">
              <Video className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No hay clips</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredClips.map((clip, idx) => {
                const statusConfig = STATUS_CONFIG[clip.status] || STATUS_CONFIG.error;
                const StatusIcon = statusConfig.icon;
                const isExpanded = expandedClip === clip.id;
                
                return (
                  <motion.div
                    key={clip.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.02 }}
                  >
                    <div 
                      className="p-4 hover:bg-accent/30 cursor-pointer transition-colors"
                      onClick={() => setExpandedClip(isExpanded ? null : clip.id)}
                    >
                      <div className="flex items-center gap-4">
                        {/* Status Icon */}
                        <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.color.split(' ')[1]}`} />
                        </div>
                        
                        {/* Main Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{clip.club_name || 'Club desconocido'}</span>
                            <Badge variant="outline" className="text-xs">
                              Cancha {clip.court_number || '?'}
                            </Badge>
                            <Badge variant="outline" className={statusConfig.color}>
                              {statusConfig.label}
                            </Badge>
                            {clip.is_fast && (
                              <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                60s Rápido
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            <span>{formatDate(clip.created_at)}</span>
                            <span>•</span>
                            <span>{formatDuration(clip.duration)}</span>
                            {clip.retry_count > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-orange-400">Intento {clip.retry_count}/3</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Expand Icon */}
                        <div className="text-muted-foreground">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border bg-accent/20"
                        >
                          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">ID del Clip</p>
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-background/50 p-1 rounded font-mono">
                                  {clip.id.slice(0, 20)}...
                                </code>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(clip.id);
                                  }}
                                >
                                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Usuario ID</p>
                              <code className="text-xs bg-background/50 p-1 rounded font-mono">
                                {clip.user_id?.slice(0, 20)}...
                              </code>
                            </div>
                            
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Club ID</p>
                              <code className="text-xs bg-background/50 p-1 rounded font-mono">
                                {clip.club_id?.slice(0, 20)}...
                              </code>
                            </div>
                            
                            {clip.hikconnect_task_id && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Hik-Connect Task</p>
                                <code className="text-xs bg-background/50 p-1 rounded font-mono">
                                  {clip.hikconnect_task_id.slice(0, 30)}...
                                </code>
                              </div>
                            )}
                            
                            {clip.error_message && (
                              <div className="md:col-span-2 lg:col-span-3">
                                <p className="text-xs text-muted-foreground mb-1">Error</p>
                                <p className="text-xs text-red-400 bg-red-500/10 p-2 rounded">
                                  {clip.error_message}
                                </p>
                              </div>
                            )}
                            
                            {clip.download_url && (
                              <div className="md:col-span-2 lg:col-span-3">
                                <p className="text-xs text-muted-foreground mb-1">URL de Descarga</p>
                                <div className="flex items-center gap-2">
                                  <a
                                    href={clip.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Abrir video
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Info */}
        <div className="text-center text-sm text-muted-foreground">
          Mostrando {filteredClips.length} de {total} clips
        </div>
      </main>
    </div>
  );
}
